import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Autocomplete, Chip, IconButton, CircularProgress, Paper, Typography, Box,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  createLead, updateLead, getLeadById, getLeadTags,
  getJoinedGreenUsers, setStoreData
} from '../../../Redux/actions';
import {
  inputLabelFocusedStyle, selectFocusedStyle, buttonStyles, datePickerSlotProps
} from '../../../theme/formStyles';

const themedTextFieldSx = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#008080' }
};

const segmentOptions = [
  'Residential Real Estate',
  'Commercial Real Estate',
  'Hospitality',
  'Healthcare',
  'Industrial & Manufacturing'
];

const stageOptions = ['New', 'Contacted', 'Met', 'Moved to Qualified Lead'];

const emptyStageUpdate = () => ({ stage: 'New', stage_date: dayjs().format('YYYY-MM-DD'), comment: '' });

export default function ManageLead({ leadId, onBack, onSaved }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [greenUsers, setGreenUsers] = useState([]);
  const [leadNameTags, setLeadNameTags] = useState([]);

  const [form, setForm] = useState({
    client_name: '',
    facility_name: '',
    opportunity_name: '',
    opportunity_name_key: '',
    segment: '',
    location: '',
    bd_owners: [],
    contact_person: '',
    designation: '',
    contact_email: '',
    contact_mobile: '',
  });

  const [stageUpdates, setStageUpdates] = useState([emptyStageUpdate()]);
  const [errors, setErrors] = useState({});

  const [visitingCardFile, setVisitingCardFile] = useState(null);
  const [visitingCardPreview, setVisitingCardPreview] = useState('');
  const [existingVisitingCardUrl, setExistingVisitingCardUrl] = useState('');

  const [openAddNameDialog, setOpenAddNameDialog] = useState(false);
  const [customNameKey, setCustomNameKey] = useState('');
  const [customNameText, setCustomNameText] = useState('');

  useEffect(() => {
    fetchGreenUsers();
    fetchLeadTags();
    if (leadId) {
      fetchLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const fetchGreenUsers = () => {
    dispatch(getJoinedGreenUsers()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        let users = res.data.allData || res.data.data || [];
        users = users.filter((u) => {
          const email = (u.user_id || '').toLowerCase();
          return email.includes('@greenvironment');
        });
        setGreenUsers(users);
      }
    });
  };

  const fetchLeadTags = () => {
    dispatch(getLeadTags()).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        setLeadNameTags(res.data.data);
      }
    });
  };

  const fetchLead = () => {
    setLoading(true);
    dispatch(getLeadById(leadId)).then((res) => {
      if (res?.status === 200 && res.data?.success) {
        const data = res.data.data;
        setForm({
          client_name: data.client_name || '',
          facility_name: data.facility_name || '',
          opportunity_name: data.opportunity_name || '',
          segment: data.segment || '',
          location: data.location || '',
          bd_owners: data.bd_owners || [],
          contact_person: data.contact_person || '',
          designation: data.designation || '',
          contact_email: data.contact_email || '',
          contact_mobile: data.contact_mobile || '',
        });
        setExistingVisitingCardUrl(data.visiting_card_url || '');
        if (data.stage_updates && data.stage_updates.length > 0) {
          setStageUpdates(data.stage_updates.map(s => ({
            stage: s.stage,
            stage_date: s.stage_date,
            comment: s.comment || ''
          })));
        }
      }
      setLoading(false);
    });
  };

  const handleOpenAddNameDialog = () => {
    setOpenAddNameDialog(true);
  };

  const handleCloseAddNameDialog = () => {
    setOpenAddNameDialog(false);
    setCustomNameKey('');
    setCustomNameText('');
  };

  const handleAddCustomName = () => {
    if (!customNameText) return;
    setForm(prev => ({
      ...prev,
      opportunity_name: customNameText,
      opportunity_name_key: customNameKey || 'OPPORTUNITY_NAME'
    }));
    // Add to local suggestions list for immediate availability
    setLeadNameTags(prev => {
      if (prev.find(t => t.tag_text === customNameText)) return prev;
      return [...prev, { tag_text: customNameText, tag_key: customNameKey || 'OPPORTUNITY_NAME' }];
    });
    handleCloseAddNameDialog();
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleVisitingCardChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVisitingCardFile(file);
    setVisitingCardPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleRemoveVisitingCard = () => {
    setVisitingCardFile(null);
    setVisitingCardPreview('');
    setExistingVisitingCardUrl('');
  };

  const addStageUpdate = () => {
    setStageUpdates([...stageUpdates, emptyStageUpdate()]);
  };

  const removeStageUpdate = (index) => {
    setStageUpdates(stageUpdates.filter((_, i) => i !== index));
  };

  const updateStageUpdate = (index, field, value) => {
    const updated = [...stageUpdates];
    updated[index][field] = value;
    setStageUpdates(updated);
  };

  const validate = () => {
    const err = {};
    if (!form.client_name) err.client_name = 'Client name is required';
    if (!form.opportunity_name) err.opportunity_name = 'Opportunity name is required';
    if (!form.segment) err.segment = 'Segment is required';
    if (form.bd_owners.length === 0) err.bd_owners = 'At least one BD Owner/SPOC is required';
    if (!form.contact_person) err.contact_person = 'Contact person is required';
    if (stageUpdates.length === 0 || stageUpdates.some(s => !s.stage || !s.stage_date)) {
      err.stage_updates = 'Each stage update needs a stage and a date';
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      ...form,
      stage_updates: stageUpdates,
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));
    if (visitingCardFile) {
      formData.append('imageFile', visitingCardFile);
    }

    setLoading(true);
    const action = leadId ? updateLead(leadId, formData) : createLead(formData);

    dispatch(action).then((res) => {
      setLoading(false);
      if ((res?.status === 200 || res?.status === 201) && res.data?.success) {
        const successMsg = res.data.message || (leadId ? 'Lead updated successfully' : 'Lead created successfully');
        dispatch(setStoreData('alertMessage', {
          msg: successMsg,
          type: 'success',
          handleClose: () => {
            dispatch(setStoreData('alertMessage', null));
            onSaved && onSaved();
          },
        }));
      } else {
        dispatch(setStoreData('alertMessage', {
          msg: res?.data?.message || 'Something went wrong',
          type: 'fail',
          handleClose: () => dispatch(setStoreData('alertMessage', null)),
        }));
      }
    });
  };

  if (loading && leadId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={onBack}>
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h6">
          {leadId ? 'Edit Lead' : 'Create Lead'}
        </Typography>
      </Box>

      <Paper sx={{ padding: 3 }}>
        <Grid container spacing={2}>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Client Name *"
              placeholder="Company name"
              value={form.client_name}
              onChange={(e) => handleChange('client_name', e.target.value)}
              error={!!errors.client_name}
              helperText={errors.client_name}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Facility"
              placeholder="Facility / site"
              value={form.facility_name}
              onChange={(e) => handleChange('facility_name', e.target.value)}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              options={[...leadNameTags, { tag_text: 'custom', isCustom: true }]}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.tag_text}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === 'string') return option.tag_text === value;
                return option.tag_text === value?.tag_text;
              }}
              value={form.opportunity_name}
              onChange={(event, newValue) => {
                if (typeof newValue === 'object' && newValue?.isCustom) {
                  handleOpenAddNameDialog();
                } else {
                  const val = typeof newValue === 'string' ? newValue : newValue?.tag_text || '';
                  const key = typeof newValue === 'object' ? newValue?.tag_key : '';
                  setForm(prev => ({
                    ...prev,
                    opportunity_name: val,
                    opportunity_name_key: key || prev.opportunity_name_key
                  }));
                }
              }}
              onInputChange={(event, newInputValue) => {
                handleChange('opportunity_name', newInputValue);
              }}
              renderOption={(props, option) => {
                if (option.isCustom) {
                  return (
                    <li {...props} style={{ justifyContent: 'center', padding: '0px' }}>
                      <Button
                        fullWidth
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAddNameDialog();
                        }}
                        sx={{ color: '#008080', px: 2, justifyContent: 'flex-start' }}
                      >
                        Add Opportunity Name
                      </Button>
                    </li>
                  );
                }
                return <li {...props}>{option.tag_text}</li>;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Opportunity Name *"
                  error={!!errors.opportunity_name}
                  helperText={errors.opportunity_name}
                  sx={themedTextFieldSx}
                />
              )}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth error={!!errors.segment}>
              <InputLabel sx={inputLabelFocusedStyle}>Segment *</InputLabel>
              <Select
                value={form.segment}
                label="Segment *"
                onChange={(e) => handleChange('segment', e.target.value)}
                sx={selectFocusedStyle}
                MenuProps={{ disableScrollLock: true }}
              >
                {segmentOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
              {errors.segment && <Typography color="error" variant="caption">{errors.segment}</Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Location (type)"
              placeholder="City / area"
              value={form.location}
              onChange={(e) => handleChange('location', e.target.value)}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              options={greenUsers.filter(u => !form.bd_owners.some(selected => selected.id === u.id))}
              getOptionLabel={(option) => option.name ? (option.user_id ? `${option.name} (${option.user_id})` : option.name) : ''}
              value={form.bd_owners}
              onChange={(e, newValue) => handleChange('bd_owners', newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name ? (option.user_id ? `${option.name} (${option.user_id})` : option.name) : ''}
                    {...getTagProps({ index })}
                    key={index}
                    size="small"
                    sx={{ backgroundColor: '#008080', color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="BD Owner / SPOC *"
                  error={!!errors.bd_owners}
                  helperText={errors.bd_owners}
                  sx={themedTextFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>Contact details</Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              Captured from the salesperson reaching out. Used to update Zoho Books when the lead is qualified.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Contact person *"
              placeholder="Full name"
              value={form.contact_person}
              onChange={(e) => handleChange('contact_person', e.target.value)}
              error={!!errors.contact_person}
              helperText={errors.contact_person}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Designation"
              placeholder="e.g. Facilities Manager"
              value={form.designation}
              onChange={(e) => handleChange('designation', e.target.value)}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <input
              id="visiting-card-upload"
              type="file"
              accept="image/*"
              hidden
              onChange={handleVisitingCardChange}
            />
            {(visitingCardPreview || existingVisitingCardUrl) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  height: 56,
                  border: '1px dashed #008080',
                  backgroundColor: 'rgba(0, 128, 128, 0.05)',
                  borderRadius: 1,
                  px: 1.5,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  component="img"
                  src={visitingCardPreview || existingVisitingCardUrl}
                  alt="Visiting card"
                  sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                />
                <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0, color: '#333' }}>
                  Visiting card attached
                </Typography>
                <IconButton
                  component="label"
                  htmlFor="visiting-card-upload"
                  size="small"
                  sx={{ color: '#008080' }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: 'error.main' }}
                  onClick={handleRemoveVisitingCard}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                component="label"
                htmlFor="visiting-card-upload"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  height: 56,
                  border: '1px dashed #008080',
                  backgroundColor: 'rgba(0, 128, 128, 0.05)',
                  borderRadius: 1,
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  color: '#008080',
                  '&:hover': { backgroundColor: 'rgba(0, 128, 128, 0.1)' },
                }}
              >
                <EditOutlinedIcon fontSize="small" />
                <Typography variant="body2" sx={{ color: '#008080' }}>
                  Upload visiting card
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Mobile number"
              placeholder="+91 ..."
              value={form.contact_mobile}
              onChange={(e) => handleChange('contact_mobile', e.target.value)}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Email ID"
              placeholder="name@company.com"
              value={form.contact_email}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              sx={themedTextFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>Stage Updates *</Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
              Add a row each time the stage moves — with its date and a comment.
            </Typography>
            {errors.stage_updates && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                {errors.stage_updates}
              </Typography>
            )}
          </Grid>

          {stageUpdates.map((update, index) => (
            <React.Fragment key={index}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={inputLabelFocusedStyle}>Stage</InputLabel>
                  <Select
                    value={update.stage}
                    label="Stage"
                    onChange={(e) => updateStageUpdate(index, 'stage', e.target.value)}
                    sx={selectFocusedStyle}
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {stageOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                  <DatePicker
                    label="Date"
                    format="DD/MM/YYYY"
                    value={update.stage_date ? dayjs(update.stage_date) : null}
                    onChange={(newVal) => updateStageUpdate(index, 'stage_date', newVal ? newVal.format('YYYY-MM-DD') : '')}
                    slotProps={datePickerSlotProps({ fullWidth: true })}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Description / comment"
                  value={update.comment}
                  onChange={(e) => updateStageUpdate(index, 'comment', e.target.value)}
                  sx={themedTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                {stageUpdates.length > 1 && (
                  <IconButton color="error" onClick={() => removeStageUpdate(index)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Grid>
            </React.Fragment>
          ))}

          <Grid item xs={12}>
            <Button
              startIcon={<AddIcon />}
              onClick={addStageUpdate}
              variant="outlined"
              size="small"
              sx={buttonStyles.outlined}
            >
              Add Stage
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="outlined" onClick={onBack} sx={buttonStyles.outlined}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={buttonStyles.contained}
              >
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (leadId ? 'Update' : 'Submit')}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Paper>

      {/* Add Opportunity Name Dialog */}
      <Dialog open={openAddNameDialog} onClose={handleCloseAddNameDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600, color: '#008080' }}>Add Opportunity Name</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Tag Key (e.g. OPP_NAME)"
              value={customNameKey}
              onChange={(e) => setCustomNameKey(e.target.value)}
              sx={{ ...themedTextFieldSx, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Opportunity Name"
              value={customNameText}
              onChange={(e) => setCustomNameText(e.target.value)}
              sx={themedTextFieldSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddNameDialog} sx={{ color: 'grey.600' }}>Cancel</Button>
          <Button
            onClick={handleAddCustomName}
            variant="contained"
            sx={{
              bgcolor: '#008080',
              '&:hover': { bgcolor: '#006666' }
            }}
          >
            Add Name
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
